// src/pages/ClassesPage/ClassesPage.jsx
import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faPlus,
	faTrashAlt,
	faTimes,
	faUser,
	faEdit,
	faGraduationCap,
	faChalkboardTeacher,
	faSave,
} from "@fortawesome/free-solid-svg-icons";

import { PageContainer } from "../../components/layout";
import { Modal, Button, Loading } from "../../components/common";
import { selectCurrentUser } from "../../store/slices/authSlice";
import {
	useGetAllClassesQuery,
	useCreateClassMutation,
	useUpdateClassMutation,
	useDeleteClassMutation,
} from "../../store/api/classApiSlice";
import {
	useGetDepartmentsQuery,
	useCreateDepartmentMutation,
	useUpdateDepartmentMutation,
	useDeleteDepartmentMutation,
} from "../../store/api/departmentApiSlice";
import { useGetSubjectsQuery } from "../../store/api/subjectApiSlice";
import { useGetTeachersQuery } from "../../store/api/teacherApiSlice";

import styles from "./ClassesPage.module.css";

const ClassesPage = () => {
	const user = useSelector(selectCurrentUser);

	// RTK Query hooks
	const {
		data: classes = [],
		isLoading: classesLoading,
		error: classesError,
	} = useGetAllClassesQuery();

	const { data: departments = [], isLoading: departmentsLoading } =
		useGetDepartmentsQuery();

	const { data: subjects = [], isLoading: subjectsLoading } =
		useGetSubjectsQuery();

	const { data: teachers = [], isLoading: teachersLoading } =
		useGetTeachersQuery();

	// Mutations
	const [createClass] = useCreateClassMutation();
	const [updateClass] = useUpdateClassMutation();
	const [deleteClass] = useDeleteClassMutation();
	const [createDepartment] = useCreateDepartmentMutation();
	const [updateDepartment] = useUpdateDepartmentMutation();
	const [deleteDepartment] = useDeleteDepartmentMutation();

	// Modal states
	const [showClassModal, setShowClassModal] = useState(false);
	const [showDepartmentModal, setShowDepartmentModal] = useState(false);
	const [showEditClassModal, setShowEditClassModal] = useState(false);
	const [showEditDepartmentModal, setShowEditDepartmentModal] = useState(false);

	// Form states
	const [selectedClassForDept, setSelectedClassForDept] = useState(null);
	const [classToEdit, setClassToEdit] = useState(null);
	const [departmentToEdit, setDepartmentToEdit] = useState(null);
	const [departmentParentClass, setDepartmentParentClass] = useState(null);

	const [newGrade, setNewGrade] = useState("");
	const [selectedSubjects, setSelectedSubjects] = useState([]);
	const [newDepartmentName, setNewDepartmentName] = useState("");
	const [selectedTeacher, setSelectedTeacher] = useState("");

	// Edit form states
	const [editGrade, setEditGrade] = useState("");
	const [editSubjects, setEditSubjects] = useState([]);
	const [editDepartmentName, setEditDepartmentName] = useState("");
	const [editTeacher, setEditTeacher] = useState("");

	// Transform data for display
	const transformedClasses = useMemo(() => {
		if (!classes.length || !subjects.length || !departments.length) return [];

		return classes.map((cls) => ({
			id: cls.id,
			grade: cls.gradeLevel,
			subjects: cls.subjects.map((subId) => {
				const subject = subjects.find((s) => s.id === subId);
				return {
					id: subId,
					name: subject ? subject.name : "Unknown Subject",
				};
			}),
			departments: departments
				.filter((dept) => dept.classId === cls.id)
				.map((dept) => {
					const teacherObj = teachers.find((t) => t.id === dept.classTeacherId);
					return {
						id: dept.id,
						name: dept.departmentName,
						teacherId: dept.classTeacherId || "",
						teacherName: teacherObj
							? `${teacherObj.firstName} ${teacherObj.lastName}`
							: "",
					};
				}),
		}));
	}, [classes, subjects, departments, teachers]);

	// Handlers
	const handleCreateClass = async () => {
		if (newGrade && selectedSubjects.length > 0) {
			const classData = {
				gradeLevel: newGrade,
				subjects: selectedSubjects,
			};

			console.log("Creating class with data:", classData);

			try {
				await createClass(classData).unwrap();
				setNewGrade("");
				setSelectedSubjects([]);
				setShowClassModal(false);
			} catch (error) {
				console.error("Error creating class:", error);
			}
		}
	};

	const handleEditClass = async () => {
		if (classToEdit && editGrade && editSubjects.length > 0) {
			try {
				await updateClass({
					id: classToEdit.id,
					gradeLevel: parseInt(editGrade),
					subjects: editSubjects,
				}).unwrap();

				setClassToEdit(null);
				setEditGrade("");
				setEditSubjects([]);
				setShowEditClassModal(false);
			} catch (error) {
				console.error("Error updating class:", error);
			}
		}
	};

	const handleCreateDepartment = async () => {
		if (selectedClassForDept && newDepartmentName) {
			try {
				await createDepartment({
					classId: selectedClassForDept.id,
					departmentName: newDepartmentName,
					classTeacherId: selectedTeacher || null,
				}).unwrap();

				setNewDepartmentName("");
				setSelectedTeacher("");
				setShowDepartmentModal(false);
			} catch (error) {
				console.error("Error creating department:", error);
			}
		}
	};

	const handleEditDepartment = async () => {
		if (departmentToEdit && departmentParentClass && editDepartmentName) {
			try {
				await updateDepartment({
					id: departmentToEdit.id,
					classId: departmentParentClass.id,
					departmentName: editDepartmentName,
					classTeacherId: editTeacher || null,
				}).unwrap();

				setDepartmentToEdit(null);
				setDepartmentParentClass(null);
				setEditDepartmentName("");
				setEditTeacher("");
				setShowEditDepartmentModal(false);
			} catch (error) {
				console.error("Error updating department:", error);
			}
		}
	};

	const handleDeleteClass = async (classId) => {
		if (
			window.confirm(
				"Are you sure you want to delete this class? This will also delete all associated departments."
			)
		) {
			try {
				await deleteClass(classId).unwrap();
			} catch (error) {
				console.error("Error deleting class:", error);
			}
		}
	};

	const handleDeleteDepartment = async (departmentId) => {
		if (window.confirm("Are you sure you want to delete this department?")) {
			try {
				await deleteDepartment(departmentId).unwrap();
			} catch (error) {
				console.error("Error deleting department:", error);
			}
		}
	};

	const handleSubjectSelect = (subjectId) => {
		const isSelected = selectedSubjects.includes(subjectId);
		if (isSelected) {
			setSelectedSubjects(selectedSubjects.filter((id) => id !== subjectId));
		} else {
			setSelectedSubjects([...selectedSubjects, subjectId]);
		}
	};

	const handleEditSubjectSelect = (subjectId) => {
		const isSelected = editSubjects.includes(subjectId);
		if (isSelected) {
			setEditSubjects(editSubjects.filter((id) => id !== subjectId));
		} else {
			setEditSubjects([...editSubjects, subjectId]);
		}
	};

	const handleAddDepartment = (classObj) => {
		setSelectedClassForDept(classObj);
		setShowDepartmentModal(true);
	};

	const handleOpenEditClass = (classObj) => {
		setClassToEdit(classObj);
		setEditGrade(classObj.grade.toString());
		setEditSubjects(classObj.subjects.map((subj) => subj.id));
		setShowEditClassModal(true);
	};

	const handleOpenEditDepartment = (classObj, departmentObj) => {
		setDepartmentParentClass(classObj);
		setDepartmentToEdit(departmentObj);
		setEditDepartmentName(departmentObj.name);
		setEditTeacher(departmentObj.teacherId || "");
		setShowEditDepartmentModal(true);
	};

	const isLoading =
		classesLoading || departmentsLoading || subjectsLoading || teachersLoading;

	if (isLoading) {
		return (
			<PageContainer>
				<Loading />
			</PageContainer>
		);
	}

	if (classesError) {
		return (
			<PageContainer>
				<div className={styles.errorState}>
					<h2>Error Loading Classes</h2>
					<p>Unable to load class data. Please try again.</p>
				</div>
			</PageContainer>
		);
	}

	return (
		<PageContainer className={styles.classesPage}>
			{/* Header */}
			<div className={styles.header}>
				<div className={styles.title}>
					<h1>
						<FontAwesomeIcon
							icon={faGraduationCap}
							className={styles.titleIcon}
						/>
						Classes Management
					</h1>
					<p className={styles.subtitle}>
						Manage classes, grades, and departments
					</p>
				</div>
				<Button variant="primary" onClick={() => setShowClassModal(true)}>
					<FontAwesomeIcon icon={faPlus} />
					Create New Class
				</Button>
			</div>

			{/* Content Area */}
			<div className={styles.content}>
				{transformedClasses.length === 0 ? (
					<div className={styles.emptyState}>
						<div className={styles.emptyIcon}>
							<FontAwesomeIcon icon={faGraduationCap} />
						</div>
						<h2>No Classes Found</h2>
						<p>
							Get started by creating your first class with subjects and
							departments
						</p>
						<Button variant="primary" onClick={() => setShowClassModal(true)}>
							<FontAwesomeIcon icon={faPlus} />
							Create Class
						</Button>
					</div>
				) : (
					<div className={styles.classesList}>
						{transformedClasses.map((cls) => (
							<div className={styles.classContainer} key={cls.id}>
								<div className={styles.classHeader}>
									<div className={styles.classInfo}>
										<h2>
											<FontAwesomeIcon
												icon={faGraduationCap}
												className={styles.headerIcon}
											/>
											Grade {cls.grade}
										</h2>
										<div className={styles.badgeContainer}>
											{cls.subjects.slice(0, 3).map((subj) => (
												<span className={styles.badge} key={subj.id}>
													{subj.name}
												</span>
											))}
											{cls.subjects.length > 3 && (
												<span className={`${styles.badge} ${styles.badgeMore}`}>
													+{cls.subjects.length - 3} more
												</span>
											)}
										</div>
									</div>
									<div className={styles.classActions}>
										<Button
											variant="secondary"
											size="sm"
											onClick={() => handleAddDepartment(cls)}
										>
											<FontAwesomeIcon icon={faPlus} />
											Add Department
										</Button>
										<button
											className={styles.iconButton}
											onClick={() => handleOpenEditClass(cls)}
											aria-label="Edit Class"
										>
											<FontAwesomeIcon icon={faEdit} />
										</button>
										<button
											className={`${styles.iconButton} ${styles.dangerButton}`}
											onClick={() => handleDeleteClass(cls.id)}
											aria-label="Delete Class"
										>
											<FontAwesomeIcon icon={faTrashAlt} />
										</button>
									</div>
								</div>

								<div className={styles.departmentsList}>
									{cls.departments.length === 0 ? (
										<div className={styles.departmentEmpty}>
											<FontAwesomeIcon
												icon={faChalkboardTeacher}
												className={styles.emptyDeptIcon}
											/>
											<p>No departments created yet</p>
											<Button
												variant="outline"
												size="sm"
												onClick={() => handleAddDepartment(cls)}
											>
												<FontAwesomeIcon icon={faPlus} />
												Add Department
											</Button>
										</div>
									) : (
										<div className={styles.departmentGrid}>
											{cls.departments.map((dept) => (
												<div className={styles.departmentCard} key={dept.id}>
													<h3 className={styles.departmentName}>{dept.name}</h3>
													<div className={styles.departmentTeacher}>
														<FontAwesomeIcon
															icon={faUser}
															className={styles.teacherIcon}
														/>
														<span>
															{dept.teacherName || "No teacher assigned"}
														</span>
													</div>
													<div className={styles.departmentActions}>
														<button
															className={styles.iconButton}
															onClick={() =>
																handleOpenEditDepartment(cls, dept)
															}
															aria-label="Edit Department"
														>
															<FontAwesomeIcon icon={faEdit} />
														</button>
														<button
															className={`${styles.iconButton} ${styles.dangerButton}`}
															onClick={() => handleDeleteDepartment(dept.id)}
															aria-label="Delete Department"
														>
															<FontAwesomeIcon icon={faTrashAlt} />
														</button>
													</div>
												</div>
											))}
										</div>
									)}
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			{/* Create Class Modal */}
			<Modal
				isOpen={showClassModal}
				onClose={() => setShowClassModal(false)}
				title="Create New Class"
				size="medium"
				footer={
					<>
						<Button
							variant="secondary"
							onClick={() => setShowClassModal(false)}
						>
							Cancel
						</Button>
						<Button
							variant="primary"
							onClick={handleCreateClass}
							disabled={!newGrade || selectedSubjects.length === 0}
						>
							<FontAwesomeIcon icon={faPlus} />
							Create Class
						</Button>
					</>
				}
			>
				<div className={styles.modalContent}>
					<div className={styles.formGroup}>
						<label htmlFor="class-grade">Grade Level</label>
						<select
							id="class-grade"
							className={styles.select}
							value={newGrade}
							onChange={(e) => setNewGrade(e.target.value)}
						>
							<option value="">Select Grade</option>
							{[...Array(12)].map((_, i) => (
								<option key={i + 1} value={i + 1}>
									Grade {i + 1}
								</option>
							))}
						</select>
					</div>
					<div className={styles.formGroup}>
						<label>Subjects</label>
						<div className={styles.subjectSelector}>
							<div className={styles.subjectGrid}>
								{subjects.map((subject) => (
									<div className={styles.subjectOption} key={subject.id}>
										<input
											type="checkbox"
											id={`subject-${subject.id}`}
											checked={selectedSubjects.includes(subject.id)}
											onChange={() => handleSubjectSelect(subject.id)}
										/>
										<label htmlFor={`subject-${subject.id}`}>
											{subject.name}
										</label>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</Modal>

			{/* Edit Class Modal */}
			<Modal
				isOpen={showEditClassModal}
				onClose={() => setShowEditClassModal(false)}
				title="Edit Class"
				size="medium"
				footer={
					<>
						<Button
							variant="secondary"
							onClick={() => setShowEditClassModal(false)}
						>
							Cancel
						</Button>
						<Button
							variant="primary"
							onClick={handleEditClass}
							disabled={!editGrade || editSubjects.length === 0}
						>
							<FontAwesomeIcon icon={faSave} />
							Save Changes
						</Button>
					</>
				}
			>
				<div className={styles.modalContent}>
					<div className={styles.formGroup}>
						<label htmlFor="edit-class-grade">Grade Level</label>
						<select
							id="edit-class-grade"
							className={styles.select}
							value={editGrade}
							onChange={(e) => setEditGrade(e.target.value)}
						>
							<option value="">Select Grade</option>
							{[...Array(12)].map((_, i) => (
								<option key={i + 1} value={i + 1}>
									Grade {i + 1}
								</option>
							))}
						</select>
					</div>
					<div className={styles.formGroup}>
						<label>Subjects</label>
						<div className={styles.subjectSelector}>
							<div className={styles.subjectGrid}>
								{subjects.map((subject) => (
									<div className={styles.subjectOption} key={subject.id}>
										<input
											type="checkbox"
											id={`edit-subject-${subject.id}`}
											checked={editSubjects.includes(subject.id)}
											onChange={() => handleEditSubjectSelect(subject.id)}
										/>
										<label htmlFor={`edit-subject-${subject.id}`}>
											{subject.name}
										</label>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</Modal>

			{/* Add Department Modal */}
			<Modal
				isOpen={showDepartmentModal}
				onClose={() => setShowDepartmentModal(false)}
				title={`Add Department - Grade ${selectedClassForDept?.grade}`}
				size="medium"
				footer={
					<>
						<Button
							variant="secondary"
							onClick={() => setShowDepartmentModal(false)}
						>
							Cancel
						</Button>
						<Button
							variant="primary"
							onClick={handleCreateDepartment}
							disabled={!newDepartmentName}
						>
							<FontAwesomeIcon icon={faPlus} />
							Add Department
						</Button>
					</>
				}
			>
				<div className={styles.modalContent}>
					<div className={styles.formGroup}>
						<label htmlFor="department-name">Department Name</label>
						<input
							id="department-name"
							type="text"
							className={styles.input}
							placeholder="Enter department name"
							value={newDepartmentName}
							onChange={(e) => setNewDepartmentName(e.target.value)}
						/>
					</div>
					<div className={styles.formGroup}>
						<label htmlFor="class-teacher">Class Teacher (Optional)</label>
						<select
							id="class-teacher"
							className={styles.select}
							value={selectedTeacher}
							onChange={(e) => setSelectedTeacher(e.target.value)}
						>
							<option value="">Select Teacher</option>
							{teachers.map((teacher) => (
								<option key={teacher.id} value={teacher.id}>
									{teacher.firstName} {teacher.lastName}
								</option>
							))}
						</select>
					</div>
				</div>
			</Modal>

			{/* Edit Department Modal */}
			<Modal
				isOpen={showEditDepartmentModal}
				onClose={() => setShowEditDepartmentModal(false)}
				title="Edit Department"
				size="medium"
				footer={
					<>
						<Button
							variant="secondary"
							onClick={() => setShowEditDepartmentModal(false)}
						>
							Cancel
						</Button>
						<Button
							variant="primary"
							onClick={handleEditDepartment}
							disabled={!editDepartmentName}
						>
							<FontAwesomeIcon icon={faSave} />
							Save Changes
						</Button>
					</>
				}
			>
				<div className={styles.modalContent}>
					<div className={styles.formGroup}>
						<label htmlFor="edit-department-name">Department Name</label>
						<input
							id="edit-department-name"
							type="text"
							className={styles.input}
							placeholder="Enter department name"
							value={editDepartmentName}
							onChange={(e) => setEditDepartmentName(e.target.value)}
						/>
					</div>
					<div className={styles.formGroup}>
						<label htmlFor="edit-class-teacher">Class Teacher</label>
						<select
							id="edit-class-teacher"
							className={styles.select}
							value={editTeacher}
							onChange={(e) => setEditTeacher(e.target.value)}
						>
							<option value="">Select Teacher</option>
							{teachers.map((teacher) => (
								<option key={teacher.id} value={teacher.id}>
									{teacher.firstName} {teacher.lastName}
								</option>
							))}
						</select>
					</div>
				</div>
			</Modal>
		</PageContainer>
	);
};

export default ClassesPage;
